import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Clock } from "lucide-react";
import axios from "axios";
import { ASNLoader } from "./ASNLoader";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/* ── Staff Card with expand ── */
const StaffCard = ({ member }) => {
  const [expanded, setExpanded] = useState(false);
  const hasBio = member.bio && member.bio.trim();
  const hasYears = member.years_with_company != null;
  const hasDetails = hasBio || hasYears;

  return (
    <div
      className="group"
      data-testid={`staff-member-${member.id}`}
    >
      {/* Card */}
      <div
        className={`cursor-pointer select-none ${hasDetails ? "" : "cursor-default"}`}
        onClick={() => hasDetails && setExpanded(!expanded)}
      >
        {/* Photo */}
        <div className="aspect-[3/4] bg-zinc-900/60 border border-white/8 overflow-hidden relative">
          {member.photo_url ? (
            <img
              src={member.photo_url}
              alt={member.name}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-zinc-900">
              <span className="text-5xl font-black font-secondary text-zinc-700 group-hover:text-zinc-500 transition-colors">
                {member.name?.[0] || "?"}
              </span>
            </div>
          )}
          {/* Hover overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        </div>

        {/* Name + role */}
        <div className="mt-3 text-center">
          <h3 className="text-white text-sm font-bold uppercase tracking-wide group-hover:text-orange-400 transition-colors">
            {member.name}
          </h3>
          {member.role && (
            <p className="text-zinc-500 text-xs mt-0.5">{member.role}</p>
          )}
          {/* Expand hint */}
          {hasDetails && (
            <ChevronDown
              className={`w-3.5 h-3.5 text-zinc-600 mx-auto mt-1.5 transition-transform duration-200 ${expanded ? "rotate-180 text-orange-400" : ""}`}
            />
          )}
        </div>
      </div>

      {/* Expandable details */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="mt-3 bg-zinc-900/60 border border-white/8 p-4 space-y-3" data-testid="staff-details-dropdown">
              {hasYears && (
                <p className="flex items-center justify-center gap-1.5 text-zinc-400 text-xs">
                  <Clock className="w-3 h-3" />
                  {member.years_with_company} year{member.years_with_company !== 1 ? "s" : ""} with ASN
                </p>
              )}
              {hasBio && (
                <p className="text-zinc-400 text-xs leading-relaxed whitespace-pre-wrap">
                  {member.bio}
                </p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ── Section Block ── */
const SectionBlock = ({ section, index }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.08 }}
      className="mb-14"
      data-testid={`staff-section-${section.id || "unsectioned"}`}
    >
      <h2 className="font-secondary text-2xl sm:text-3xl font-bold uppercase tracking-tight text-white mb-6 text-center">
        {section.name}
      </h2>
      <div
        className="grid gap-6"
        style={{ gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))" }}
      >
        {section.members.map((m) => (
          <StaffCard key={m.id} member={m} />
        ))}
      </div>
    </motion.div>
  );
};

/* ── Main Page ── */
export const StaffPage = () => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/staff/sections/public`)
      .then((res) => setSections(res.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <ASNLoader size="fullscreen" />
      </div>
    );
  }

  const totalMembers = sections.reduce((sum, s) => sum + (s.members?.length || 0), 0);

  return (
    <div className="min-h-screen bg-[#050505]">
      <main className="pt-10 pb-20 px-4 md:px-8">
        <div className="max-w-5xl mx-auto">
          <h1 className="font-secondary text-4xl sm:text-5xl font-bold uppercase tracking-tight text-white mb-4 text-center">
            Our Staff
          </h1>
          <p className="text-zinc-500 text-sm text-center mb-12 max-w-md mx-auto">
            The people behind Antler Sports Network
          </p>

          {totalMembers === 0 ? (
            <p className="text-center text-zinc-600 py-16">No staff members to show</p>
          ) : (
            sections.map((section, i) => (
              <SectionBlock key={section.id || "unsectioned"} section={section} index={i} />
            ))
          )}
        </div>
      </main>
    </div>
  );
};

export default StaffPage;
